import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

import ContactModal from "../../../../Assets/Common/ContactModal";

const HeroSection = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);

  if (!data) return null;

  /* ============================================================
     CONTACT MODAL DATA
  ============================================================= */

  const contactService = "Legal Services";

  /*
   * Get the current page slug from the URL.
   *
   * Example:
   *
   * /services/legal-services/business-registration
   *
   * returns:
   *
   * business-registration
   */

  const currentSlug = window.location.pathname
    .split("/")
    .filter(Boolean)
    .pop()
    ?.toLowerCase();

  /* ============================================================
     LEGAL SUB-SERVICE MAP
  ============================================================= */

  const legalSubServiceMap = {
    "business-registration": "Business Registration",
    "company-registration": "Company Registration",
    "taxation-services": "Taxation Services",
    "iso-certification": "ISO Certification",
    "ipr-services": "IPR Services",
  };

  /* ============================================================
     GET CURRENT SUB-SERVICE
  ============================================================= */

  const contactSubService =
    legalSubServiceMap[currentSlug] || "";

  /*
   * DEBUG
   *
   * Open browser console and you should see:
   *
   * Current Slug: business-registration
   * Contact Service: Legal Services
   * Contact Sub-Service: Business Registration
   */

  console.log("Current Slug:", currentSlug);
  console.log("Contact Service:", contactService);
  console.log(
    "Contact Sub-Service:",
    contactSubService
  );

  /* ============================================================
     ANIMATION VARIANTS
  ============================================================= */

  const containerVariants = {
    hidden: {},

    show: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      x: -40,
    },

    show: {
      opacity: 1,
      x: 0,

      transition: {
        duration: 0.65,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: {
      opacity: 0,
      scale: 0.94,
      x: 50,
    },

    show: {
      opacity: 1,
      scale: 1,
      x: 0,

      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      {/* ==========================================================
          HERO SECTION
      =========================================================== */}

      <section
        className="
          relative
          overflow-hidden
          theme-bg-primary
          py-8

          sm:py-12
          md:py-16
          lg:py-20
          xl:py-24
        "
      >
        {/* ========================================================
            BACKGROUND GLOW - LEFT
        ========================================================= */}

        <div
          className="
            pointer-events-none
            absolute
            -left-44
            top-20
            h-[260px]
            w-[260px]
            rounded-full
            bg-orange-500/10
            blur-[150px]

            sm:h-[320px]
            sm:w-[320px]

            lg:h-[360px]
            lg:w-[360px]
            lg:blur-[180px]
          "
        />

        {/* ========================================================
            BACKGROUND GLOW - RIGHT
        ========================================================= */}

        <div
          className="
            pointer-events-none
            absolute
            bottom-0
            right-0
            h-[260px]
            w-[260px]
            rounded-full
            bg-orange-500/10
            blur-[150px]

            sm:h-[320px]
            sm:w-[320px]

            lg:h-[360px]
            lg:w-[360px]
            lg:blur-[180px]
          "
        />

        {/* ========================================================
            BACKGROUND GRID
        ========================================================= */}

        <div
          className="
            pointer-events-none
            absolute
            inset-0
            bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)]
            bg-[size:70px_70px]
          "
        />

        {/* ========================================================
            MAIN CONTAINER
        ========================================================= */}

        <div
          className="
            relative
            mx-auto
            max-w-7xl
            px-5

            sm:px-6
            lg:px-8
          "
        >
          <div
            className="
              grid
              items-center
              gap-10

              lg:grid-cols-[1.15fr_0.85fr]
              lg:gap-14
              lg:min-h-[650px]

              xl:gap-16
            "
          >
            {/* ====================================================
                LEFT CONTENT
            ==================================================== */}

            <AnimatePresence mode="wait">
              <motion.div
                key={data.title}
                variants={containerVariants}
                initial="hidden"
                animate="show"
                exit={{
                  opacity: 0,
                  x: 40,
                }}
                className="
                  order-2
                  flex
                  h-full
                  flex-col
                  justify-center
                  py-2

                  lg:order-1
                "
              >
                {/* ==================================================
                    BADGE
                ================================================== */}

                <motion.div variants={itemVariants}>
                  <span
                    className="
                      inline-flex
                      w-fit
                      items-center
                      rounded-full
                      border
                      border-orange-500/25
                      bg-orange-500/10
                      px-4
                      py-2
                      text-[10px]
                      font-semibold
                      uppercase
                      tracking-[2px]
                      text-orange-400

                      sm:px-5
                      sm:text-[11px]
                      sm:tracking-[3px]

                      md:text-xs
                    "
                  >
                    Legal Services
                  </span>
                </motion.div>

                {/* ==================================================
                    HEADING
                ================================================== */}

                <motion.h1
                  variants={itemVariants}
                  className="
                    mt-5
                    max-w-3xl
                    text-[34px]
                    font-bold
                    leading-[1.08]
                    theme-text-primary

                    sm:mt-6
                    sm:text-[42px]

                    md:text-[52px]

                    lg:text-[58px]

                    xl:text-[64px]
                  "
                >
                  {data.title}
                </motion.h1>

                {/* ==================================================
                    SUBTITLE
                ================================================== */}

                <motion.p
                  variants={itemVariants}
                  className="
                    mt-5
                    max-w-2xl
                    text-[15px]
                    leading-7
                    theme-text-muted

                    sm:mt-6
                    sm:text-base

                    lg:text-lg
                    lg:leading-8
                  "
                >
                  {data.subtitle}
                </motion.p>

                {/* ==================================================
                    TRUST POINTS
                ================================================== */}

                <motion.div
                  variants={itemVariants}
                  className="
                    mt-7
                    flex
                    flex-wrap
                    gap-x-6
                    gap-y-4

                    sm:mt-8
                  "
                >
                  {[
                    "Government Approved",
                    "100% Compliance",
                    "Expert Legal Team",
                  ].map((point) => (
                    <div
                      key={point}
                      className="
                        flex
                        items-center
                        gap-2
                      "
                    >
                      <CheckCircle2
                        size={18}
                        className="
                          shrink-0
                          text-orange-500
                        "
                      />

                      <span
                        className="
                          text-sm
                          theme-text-secondary
                        "
                      >
                        {point}
                      </span>
                    </div>
                  ))}
                </motion.div>

                {/* ==================================================
                    CTA
                ================================================== */}

                <motion.div
                  variants={itemVariants}
                  className="
                    mt-8
                    sm:mt-10
                  "
                >
                  <motion.button
                    type="button"
                    whileHover={{
                      scale: 1.03,
                    }}
                    whileTap={{
                      scale: 0.97,
                    }}
                    onClick={() => setOpenModal(true)}
                    className="
                      group
                      inline-flex
                      items-center
                      gap-3
                      rounded-full
                      brand-bg
                      px-6
                      py-3.5
                      text-sm
                      font-semibold
                      text-white
                      transition-all
                      duration-300
                      hover:bg-orange-600

                      sm:px-8
                      sm:py-4
                      sm:text-base
                    "
                  >
                    <span>
                      {data.buttonText ||
                        "Book Free Consultation"}
                    </span>

                    <ArrowRight
                      size={18}
                      className="
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                      "
                    />
                  </motion.button>
                </motion.div>
              </motion.div>
            </AnimatePresence>

            {/* ====================================================
                RIGHT IMAGE
            ==================================================== */}

            <AnimatePresence mode="wait">
              <motion.div
                key={data.image}
                variants={imageVariants}
                initial="hidden"
                animate="show"
                exit={{
                  opacity: 0,
                  scale: 0.96,
                  x: -40,
                }}
                className="
                  relative
                  order-1
                  flex
                  h-full
                  items-center
                  justify-center

                  lg:order-2
                "
              >
                <div
                  className="
                    relative
                    w-full
                    max-w-[340px]
                    overflow-hidden
                    rounded-[18px]
                    border
                    border-[var(--border-primary)]
                    theme-card
                    shadow-[0_30px_80px_var(--shadow-color)]

                    sm:max-w-[430px]

                    md:max-w-[520px]

                    lg:max-w-full
                  "
                >
                  {data.image && (
                    <motion.img
                      key={data.image}
                      src={data.image}
                      alt={data.title}
                      initial={{
                        scale: 1.08,
                      }}
                      animate={{
                        scale: 1,
                      }}
                      transition={{
                        duration: 1.2,
                        ease: "easeOut",
                      }}
                      className="
                        h-[160px]
                        w-full
                        object-cover
                        object-center

                        sm:h-[240px]

                        md:h-[330px]

                        lg:h-[460px]

                        xl:h-[520px]
                      "
                    />
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ============================================================
          CONTACT MODAL
      ============================================================= */}

      <ContactModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        initialService={contactService}
        initialSubService={contactSubService}
        lockService={true}
        lockSubService={Boolean(contactSubService)}
      />
    </>
  );
};

export default HeroSection;